/**
 * Agents Domain Seed Data
 *
 * Contains all seed data for Agent Service (8):
 * - Agent Configurations
 * - Agent Sessions
 * - Conversation Messages
 * - Session Memory
 *
 * @see PR #264 - AI Data Chat Enhancement
 */

import { seedId } from '../utils';
import { BUSINESS_IDS } from './tenants';
import { USER_IDS } from './users';

// =============================================================================
// AGENT CONFIG IDs
// =============================================================================

export const AGENT_CONFIG_IDS = {
  // Data analysis agents
  DATA_CHAT_ASSISTANT: seedId('agent-data-chat', 1),
  PIPELINE_ANALYST: seedId('agent-pipeline-analyst', 1),

  // Loan processing agents
  LOAN_ASSISTANT: seedId('agent-loan-assistant', 1),
  UNDERWRITING_COPILOT: seedId('agent-uw-copilot', 1),

  // Document agents
  DOCUMENT_ANALYZER: seedId('agent-doc-analyzer', 1),
  CONTRACT_REVIEWER: seedId('agent-contract-reviewer', 1),

  // Customer service agents
  SUPPORT_BOT: seedId('agent-support-bot', 1),
} as const;

// =============================================================================
// PORTAL AGENT IDs (API Gateway Agent model)
// =============================================================================

/**
 * Portal-specific agents for Command Palette AI Chat.
 * These are platform-level built-in agents managed by Agent Service (8).
 *
 * The API Gateway recognizes these slugs and bypasses DB lookup,
 * returning a virtual agent config so the invocation proceeds
 * directly to the Agent Service via gRPC.
 *
 * Referenced by the portal's CommandPaletteProvider sendFn.
 */
export const PORTAL_AGENT_IDS = {
  /** Business Portal AI Assistant */
  BUSINESS_ASSISTANT: 'business-assistant',
  /** Admin Portal AI Assistant */
  ADMIN_ASSISTANT: 'admin-assistant',
  /** Consumer Portal AI Assistant */
  CONSUMER_ASSISTANT: 'consumer-assistant',
} as const;

export type PortalAgentId = (typeof PORTAL_AGENT_IDS)[keyof typeof PORTAL_AGENT_IDS];

export type AgentConfigId = (typeof AGENT_CONFIG_IDS)[keyof typeof AGENT_CONFIG_IDS];

// =============================================================================
// AGENT SESSION IDs
// =============================================================================

export const AGENT_SESSION_IDS = {
  // Rachel's sessions
  RACHEL_DATA_SESSION_1: seedId('agent-session-rachel', 1),
  RACHEL_DATA_SESSION_2: seedId('agent-session-rachel', 2),
  RACHEL_PIPELINE_SESSION: seedId('agent-session-rachel-pipe', 1),

  // Marcus's sessions (consumer)
  MARCUS_SUPPORT_SESSION: seedId('agent-session-marcus', 1),
} as const;

export type AgentSessionId = (typeof AGENT_SESSION_IDS)[keyof typeof AGENT_SESSION_IDS];

// =============================================================================
// CONVERSATION MESSAGE IDs
// =============================================================================

export const CONVERSATION_MESSAGE_IDS = {
  // Rachel's data chat messages
  RACHEL_MSG_1: seedId('conv-msg-rachel', 1),
  RACHEL_MSG_2: seedId('conv-msg-rachel', 2),
  RACHEL_MSG_3: seedId('conv-msg-rachel', 3),
  RACHEL_MSG_4: seedId('conv-msg-rachel', 4),
} as const;

export type ConversationMessageId = (typeof CONVERSATION_MESSAGE_IDS)[keyof typeof CONVERSATION_MESSAGE_IDS];

// =============================================================================
// SESSION MEMORY IDs
// =============================================================================

export const SESSION_MEMORY_IDS = {
  RACHEL_PREF_1: seedId('session-memory-rachel', 1),
  RACHEL_FACT_1: seedId('session-memory-rachel', 2),
  RACHEL_CONTEXT_1: seedId('session-memory-rachel', 3),
} as const;

export type SessionMemoryId = (typeof SESSION_MEMORY_IDS)[keyof typeof SESSION_MEMORY_IDS];

// =============================================================================
// PORTAL AGENT DATA TYPE (API Gateway Agent model)
// =============================================================================

/**
 * Matches API Gateway's Agent Prisma model
 * Used for seeding portal AI assistants
 */
export interface PortalAgentSeedData {
  id: string;
  businessId: string;
  name: string;
  description: string;
  status: 'draft' | 'active' | 'paused' | 'archived';
  isActive: boolean;
  config: {
    model: string;
    provider: 'openai' | 'anthropic' | 'google_vertex' | 'azure_openai';
    temperature: number;
    maxTokens: number;
    topP: number;
    systemPrompt: string;
    enableRAG: boolean;
    enableTools: boolean;
    tools?: string[];
  };
  createdBy: string;
}

// =============================================================================
// PORTAL AGENT DATA
// =============================================================================

export const PORTAL_AGENTS: Record<keyof typeof PORTAL_AGENT_IDS, PortalAgentSeedData> = {
  BUSINESS_ASSISTANT: {
    id: PORTAL_AGENT_IDS.BUSINESS_ASSISTANT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Business Portal Assistant',
    description:
      'AI assistant for business portal users. Helps with loan processing, pipeline management, and operational questions.',
    status: 'active',
    isActive: true,
    config: {
      model: 'gpt-4o-mini',
      provider: 'openai',
      temperature: 0.7,
      maxTokens: 4096,
      topP: 0.9,
      systemPrompt: `You are a helpful AI assistant for {{businessName}}. You help loan officers, underwriters, and operations staff with their daily tasks.

You can help with:
- Understanding loan status and pipeline metrics
- Answering questions about company policies and procedures
- Providing guidance on document requirements
- Explaining underwriting criteria
- Assisting with workflow questions

Always be professional, accurate, and helpful. If you're unsure about something, say so rather than guessing.`,
      enableRAG: true,
      enableTools: true,
      tools: [],
    },
    createdBy: USER_IDS.SUPER_ADMIN,
  },
  ADMIN_ASSISTANT: {
    id: PORTAL_AGENT_IDS.ADMIN_ASSISTANT,
    businessId: BUSINESS_IDS.PLATFORM_ADMIN,
    name: 'Admin Portal Assistant',
    description:
      'AI assistant for platform administrators. Helps with platform management, tenant configuration, and system administration.',
    status: 'active',
    isActive: true,
    config: {
      model: 'gpt-4o-mini',
      provider: 'openai',
      temperature: 0.5,
      maxTokens: 4096,
      topP: 0.9,
      systemPrompt: `You are a helpful AI assistant for platform administrators. You help with platform-wide operations, tenant management, and system configuration.

You can help with:
- Platform configuration and settings
- Tenant and realm management
- User administration across businesses
- System monitoring and health checks
- Billing and subscription management

Always be precise and careful when discussing administrative operations. Recommend best practices and warn about potential impacts of changes.`,
      enableRAG: true,
      enableTools: true,
      tools: [],
    },
    createdBy: USER_IDS.SUPER_ADMIN,
  },
  CONSUMER_ASSISTANT: {
    id: PORTAL_AGENT_IDS.CONSUMER_ASSISTANT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Consumer Portal Assistant',
    description:
      'AI assistant for borrowers and consumers. Helps with loan status, document questions, and general support.',
    status: 'active',
    isActive: true,
    config: {
      model: 'gpt-4o-mini',
      provider: 'openai',
      temperature: 0.7,
      maxTokens: 2048,
      topP: 0.9,
      systemPrompt: `You are a friendly AI assistant for borrowers using {{businessName}}. You help borrowers understand their loan status and answer common questions.

You can help with:
- Explaining loan status and next steps
- Answering questions about document requirements
- Providing general information about the lending process
- Helping navigate the consumer portal
- Calling platform tools (CRM, cases, partners, etc.) when a question needs live data

When a user's question requires real platform data, prefer calling the relevant tool over guessing or deflecting. Authorization is enforced at the API layer, so any tool call you make will already be scoped to what the caller is allowed to see.

Always be friendly, clear, and helpful. Use simple language and avoid technical jargon when speaking to borrowers. Never invent specific financial advice — when a tool returns data, summarise it accurately; when it doesn't, say so.`,
      enableRAG: true,
      enableTools: true,
      tools: [],
    },
    createdBy: USER_IDS.SUPER_ADMIN,
  },
};

// =============================================================================
// DATA TYPES
// =============================================================================

export interface AgentConfigSeedData {
  id: string;
  businessId: string;
  agentId: string;
  name: string;
  description: string;
  modelId: string;
  instructions: string[];
  tools: Record<string, { enabled: boolean; config?: Record<string, unknown> }>;
  status: 'ACTIVE' | 'INACTIVE' | 'CREATING' | 'UPDATING' | 'DELETING' | 'FAILED';
  temperature?: number;
  maxTokens?: number;
}

export interface AgentSessionSeedData {
  id: string;
  businessId: string;
  sessionId: string;
  userId: string;
  agentId?: string;
  status: 'ACTIVE' | 'PAUSED' | 'COMPLETED' | 'EXPIRED' | 'ERROR';
  summary?: string;
  metadata?: Record<string, unknown>;
  totalInputTokens?: number;
  totalOutputTokens?: number;
}

export interface ConversationMessageSeedData {
  id: string;
  businessId: string;
  sessionId: string;
  role: 'user' | 'assistant' | 'system' | 'tool';
  content: string;
  toolCalls?: Record<string, unknown>[];
  toolResults?: Record<string, unknown>[];
  inputTokens?: number;
  outputTokens?: number;
  metadata?: Record<string, unknown>;
  wasFiltered?: boolean;
  filterReason?: string;
}

export interface SessionMemorySeedData {
  id: string;
  businessId: string;
  userId: string;
  sessionId?: string;
  content: string;
  type: 'PREFERENCE' | 'FACT' | 'INSTRUCTION' | 'CONTEXT' | 'FEEDBACK' | 'ENTITY';
  source: 'EXPLICIT' | 'EXTRACTED' | 'INFERRED' | 'SYSTEM';
  status: 'ACTIVE' | 'ARCHIVED' | 'SUPERSEDED' | 'DELETED';
  importance: number; // 0.0 - 1.0
  metadata?: Record<string, unknown>;
}

// =============================================================================
// AGENT CONFIG DATA
// =============================================================================

export const AGENT_CONFIGS: Record<keyof typeof AGENT_CONFIG_IDS, AgentConfigSeedData> = {
  DATA_CHAT_ASSISTANT: {
    id: AGENT_CONFIG_IDS.DATA_CHAT_ASSISTANT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    agentId: 'data-chat-v1',
    name: 'Data Chat Assistant',
    description: 'AI-powered data analysis and visualization assistant for pipeline and CRM data',
    modelId: 'claude-3-5-sonnet',
    instructions: [
      'You are a data analysis assistant for a mortgage lending platform.',
      'Help users understand their pipeline, lead, and deal data.',
      'Provide insights and visualizations when requested.',
      'Always explain your analysis methodology.',
      'When showing numbers, format them appropriately (currency, percentages).',
    ],
    tools: {
      queryDatabase: { enabled: true, config: { maxRows: 1000 } },
      createChart: { enabled: true, config: { chartTypes: ['bar', 'line', 'pie', 'scatter'] } },
      exportData: { enabled: true, config: { formats: ['csv', 'xlsx'] } },
      calculateMetrics: { enabled: true },
    },
    status: 'ACTIVE',
    temperature: 0.3,
    maxTokens: 4096,
  },
  PIPELINE_ANALYST: {
    id: AGENT_CONFIG_IDS.PIPELINE_ANALYST,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    agentId: 'pipeline-analyst-v1',
    name: 'Pipeline Analyst',
    description: 'Specialized agent for loan pipeline analysis and forecasting',
    modelId: 'claude-3-5-sonnet',
    instructions: [
      'You specialize in loan pipeline analysis.',
      'Provide conversion rate analysis, stage bottleneck identification, and forecasting.',
      'Alert users to deals that may need attention.',
    ],
    tools: {
      queryDatabase: { enabled: true },
      calculateMetrics: { enabled: true },
      createChart: { enabled: true },
      predictConversion: { enabled: true },
    },
    status: 'ACTIVE',
    temperature: 0.2,
    maxTokens: 4096,
  },
  LOAN_ASSISTANT: {
    id: AGENT_CONFIG_IDS.LOAN_ASSISTANT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    agentId: 'loan-assistant-v1',
    name: 'Loan Processing Assistant',
    description: 'Assists loan officers with application processing and borrower communication',
    modelId: 'claude-3-5-sonnet',
    instructions: [
      'You are a loan processing assistant.',
      'Help loan officers with document verification, condition clearing, and borrower communication.',
      'Always maintain compliance with lending regulations.',
    ],
    tools: {
      documentAnalysis: { enabled: true },
      conditionChecker: { enabled: true },
      draftEmail: { enabled: true },
      calculateLoanTerms: { enabled: true },
    },
    status: 'ACTIVE',
    temperature: 0.4,
    maxTokens: 4096,
  },
  UNDERWRITING_COPILOT: {
    id: AGENT_CONFIG_IDS.UNDERWRITING_COPILOT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    agentId: 'uw-copilot-v1',
    name: 'Underwriting Copilot',
    description: 'Assists underwriters with risk assessment and decision support',
    modelId: 'claude-3-5-sonnet',
    instructions: [
      'You assist underwriters with risk assessment.',
      'Analyze borrower financials, property valuations, and market data.',
      'Flag potential issues and suggest conditions.',
      'Always explain your risk reasoning.',
    ],
    tools: {
      riskScoring: { enabled: true },
      comparableAnalysis: { enabled: true },
      financialRatios: { enabled: true },
      guidelineChecker: { enabled: true },
    },
    status: 'ACTIVE',
    temperature: 0.2,
    maxTokens: 8192,
  },
  DOCUMENT_ANALYZER: {
    id: AGENT_CONFIG_IDS.DOCUMENT_ANALYZER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    agentId: 'doc-analyzer-v1',
    name: 'Document Analyzer',
    description: 'Extracts and validates information from loan documents',
    modelId: 'claude-3-5-sonnet',
    instructions: [
      'You analyze and extract data from loan documents.',
      'Identify document types, extract key fields, and flag discrepancies.',
      'Validate document authenticity indicators.',
    ],
    tools: {
      ocrExtraction: { enabled: true },
      documentClassification: { enabled: true },
      fieldValidation: { enabled: true },
      fraudDetection: { enabled: true },
    },
    status: 'ACTIVE',
    temperature: 0.1,
    maxTokens: 4096,
  },
  CONTRACT_REVIEWER: {
    id: AGENT_CONFIG_IDS.CONTRACT_REVIEWER,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    agentId: 'contract-reviewer-v1',
    name: 'Contract Reviewer',
    description: 'Reviews loan documents and contracts for compliance',
    modelId: 'claude-3-5-sonnet',
    instructions: [
      'You review contracts and loan documents.',
      'Identify key terms, risks, and compliance issues.',
      'Summarize important clauses for review.',
    ],
    tools: {
      clauseExtraction: { enabled: true },
      complianceCheck: { enabled: true },
      riskIdentification: { enabled: true },
    },
    status: 'ACTIVE',
    temperature: 0.2,
    maxTokens: 8192,
  },
  SUPPORT_BOT: {
    id: AGENT_CONFIG_IDS.SUPPORT_BOT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    agentId: 'support-bot-v1',
    name: 'Support Bot',
    description: 'Handles common borrower questions and support requests',
    modelId: 'claude-3-5-haiku',
    instructions: [
      'You are a friendly support assistant for borrowers.',
      'Answer common questions about loan status, documents, and payments.',
      'Escalate complex issues to human support.',
      'Never provide specific financial advice.',
    ],
    tools: {
      lookupLoanStatus: { enabled: true },
      checkDocumentStatus: { enabled: true },
      scheduleCallback: { enabled: true },
      createTicket: { enabled: true },
    },
    status: 'ACTIVE',
    temperature: 0.5,
    maxTokens: 2048,
  },
};

// =============================================================================
// AGENT SESSION DATA
// =============================================================================

export const AGENT_SESSIONS: Record<keyof typeof AGENT_SESSION_IDS, AgentSessionSeedData> = {
  RACHEL_DATA_SESSION_1: {
    id: AGENT_SESSION_IDS.RACHEL_DATA_SESSION_1,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    sessionId: 'session-rachel-data-001',
    userId: USER_IDS.RACHEL_MARTINEZ,
    agentId: AGENT_CONFIG_IDS.DATA_CHAT_ASSISTANT,
    status: 'COMPLETED',
    summary: 'Analyzed Q4 2025 pipeline performance, identified bottleneck in underwriting stage',
    totalInputTokens: 2500,
    totalOutputTokens: 3800,
  },
  RACHEL_DATA_SESSION_2: {
    id: AGENT_SESSION_IDS.RACHEL_DATA_SESSION_2,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    sessionId: 'session-rachel-data-002',
    userId: USER_IDS.RACHEL_MARTINEZ,
    agentId: AGENT_CONFIG_IDS.DATA_CHAT_ASSISTANT,
    status: 'ACTIVE',
    summary: 'Reviewing lead conversion rates by source',
    totalInputTokens: 850,
    totalOutputTokens: 1200,
  },
  RACHEL_PIPELINE_SESSION: {
    id: AGENT_SESSION_IDS.RACHEL_PIPELINE_SESSION,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    sessionId: 'session-rachel-pipeline-001',
    userId: USER_IDS.RACHEL_MARTINEZ,
    agentId: AGENT_CONFIG_IDS.PIPELINE_ANALYST,
    status: 'COMPLETED',
    summary: 'Forecasted January 2026 funding volume at $12.5M based on current pipeline',
    totalInputTokens: 1800,
    totalOutputTokens: 2500,
  },
  MARCUS_SUPPORT_SESSION: {
    id: AGENT_SESSION_IDS.MARCUS_SUPPORT_SESSION,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    sessionId: 'session-marcus-support-001',
    userId: USER_IDS.RACHEL_MARTINEZ, // Support bot logs under staff user
    agentId: AGENT_CONFIG_IDS.SUPPORT_BOT,
    status: 'COMPLETED',
    summary: 'Borrower inquired about loan status and document requirements',
    metadata: { borrowerEmail: 'marcus.thompson@investorpro.com' },
    totalInputTokens: 450,
    totalOutputTokens: 680,
  },
};

// =============================================================================
// CONVERSATION MESSAGE DATA
// =============================================================================

export const CONVERSATION_MESSAGES: Record<keyof typeof CONVERSATION_MESSAGE_IDS, ConversationMessageSeedData> = {
  RACHEL_MSG_1: {
    id: CONVERSATION_MESSAGE_IDS.RACHEL_MSG_1,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    sessionId: AGENT_SESSION_IDS.RACHEL_DATA_SESSION_1,
    role: 'user',
    content: 'Show me the pipeline performance for Q4 2025. What was our conversion rate from application to funding?',
    inputTokens: 28,
  },
  RACHEL_MSG_2: {
    id: CONVERSATION_MESSAGE_IDS.RACHEL_MSG_2,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    sessionId: AGENT_SESSION_IDS.RACHEL_DATA_SESSION_1,
    role: 'assistant',
    content: `Based on the Q4 2025 data, here's your pipeline performance analysis:

**Overall Conversion Rate: 34.2%** (Application to Funding)

**Stage Breakdown:**
- Application → Document Collection: 89%
- Document Collection → Underwriting: 72%
- Underwriting → Approval: 68%
- Approval → Funding: 78%

**Key Insight:** The underwriting stage shows the lowest conversion rate at 68%. This suggests a potential bottleneck that may warrant investigation.

Would you like me to drill down into the underwriting stage to identify specific issues?`,
    outputTokens: 145,
    toolCalls: [{ name: 'queryDatabase', args: { query: 'pipeline_metrics', dateRange: 'Q4_2025' } }],
  },
  RACHEL_MSG_3: {
    id: CONVERSATION_MESSAGE_IDS.RACHEL_MSG_3,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    sessionId: AGENT_SESSION_IDS.RACHEL_DATA_SESSION_1,
    role: 'user',
    content: 'Yes, what are the main reasons for underwriting denials?',
    inputTokens: 12,
  },
  RACHEL_MSG_4: {
    id: CONVERSATION_MESSAGE_IDS.RACHEL_MSG_4,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    sessionId: AGENT_SESSION_IDS.RACHEL_DATA_SESSION_1,
    role: 'assistant',
    content: `**Top Underwriting Denial Reasons (Q4 2025):**

1. **Insufficient Experience (28%)** - Borrowers with < 3 completed deals
2. **DSCR Below Threshold (22%)** - Property cash flow didn't meet 1.25x requirement
3. **Appraisal Issues (18%)** - Value came in lower than expected
4. **Credit Concerns (15%)** - Recent derogatories or thin file
5. **Other (17%)** - Various documentation and verification issues

**Recommendation:** Consider implementing a pre-qualification step to filter borrowers with insufficient experience earlier in the pipeline.`,
    outputTokens: 168,
  },
};

// =============================================================================
// SESSION MEMORY DATA
// =============================================================================

export const SESSION_MEMORIES: Record<keyof typeof SESSION_MEMORY_IDS, SessionMemorySeedData> = {
  RACHEL_PREF_1: {
    id: SESSION_MEMORY_IDS.RACHEL_PREF_1,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    userId: USER_IDS.RACHEL_MARTINEZ,
    content: 'User prefers data visualizations in bar chart format over tables',
    type: 'PREFERENCE',
    source: 'EXTRACTED',
    status: 'ACTIVE',
    importance: 0.7,
    metadata: { category: 'visualization', confidence: 0.85 },
  },
  RACHEL_FACT_1: {
    id: SESSION_MEMORY_IDS.RACHEL_FACT_1,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    userId: USER_IDS.RACHEL_MARTINEZ,
    content: 'Rachel is the sales manager responsible for the Austin and Dallas markets',
    type: 'FACT',
    source: 'EXPLICIT',
    status: 'ACTIVE',
    importance: 0.9,
    metadata: { category: 'role', entities: ['Austin', 'Dallas'] },
  },
  RACHEL_CONTEXT_1: {
    id: SESSION_MEMORY_IDS.RACHEL_CONTEXT_1,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    userId: USER_IDS.RACHEL_MARTINEZ,
    sessionId: AGENT_SESSION_IDS.RACHEL_DATA_SESSION_1,
    content: 'User is investigating Q4 pipeline bottlenecks, particularly in underwriting',
    type: 'CONTEXT',
    source: 'INFERRED',
    status: 'ACTIVE',
    importance: 0.8,
    metadata: { sessionContext: true, topic: 'pipeline_analysis' },
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

export function getAllAgentConfigs(workspace?: string): AgentConfigSeedData[] {
  const configs = Object.values(AGENT_CONFIGS);
  if (!workspace) return configs;
  return configs.map((c) => ({ ...c, name: `${c.name} [${workspace}]` }));
}

export function getAllAgentSessions(): AgentSessionSeedData[] {
  return Object.values(AGENT_SESSIONS);
}

export function getAllConversationMessages(): ConversationMessageSeedData[] {
  return Object.values(CONVERSATION_MESSAGES);
}

export function getAllSessionMemories(): SessionMemorySeedData[] {
  return Object.values(SESSION_MEMORIES);
}

export function getAgentConfigsByBusiness(businessId: string): AgentConfigSeedData[] {
  return Object.values(AGENT_CONFIGS).filter((c) => c.businessId === businessId);
}

export function getSessionsByUser(userId: string): AgentSessionSeedData[] {
  return Object.values(AGENT_SESSIONS).filter((s) => s.userId === userId);
}

export function getMessagesBySession(sessionId: string): ConversationMessageSeedData[] {
  return Object.values(CONVERSATION_MESSAGES).filter((m) => m.sessionId === sessionId);
}

export function getMemoriesByUser(userId: string): SessionMemorySeedData[] {
  return Object.values(SESSION_MEMORIES).filter((m) => m.userId === userId);
}

// =============================================================================
// PORTAL AGENT HELPER FUNCTIONS
// =============================================================================

/**
 * Get all portal agents for seeding API Gateway Agent model
 */
export function getAllPortalAgents(): PortalAgentSeedData[] {
  return Object.values(PORTAL_AGENTS);
}

/**
 * Get portal agent by ID
 */
export function getPortalAgentById(agentId: string): PortalAgentSeedData | undefined {
  return Object.values(PORTAL_AGENTS).find((a) => a.id === agentId);
}

/**
 * Get portal agents by business
 */
export function getPortalAgentsByBusiness(businessId: string): PortalAgentSeedData[] {
  return Object.values(PORTAL_AGENTS).filter((a) => a.businessId === businessId);
}
